import { useState } from 'react';
import {
    Box, Container, Typography, Grid, Card, CardContent,
    TextField, Button, Alert, MenuItem, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SendIcon from '@mui/icons-material/Send';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const contactInfo = [
    {
        Icon: EmailIcon,
        title: 'Email',
        content: <a href="mailto:hello@tinyrewards.net" style={{ color: 'inherit', textDecoration: 'none' }}>hello@tinyrewards.net</a>,
    },
    {
        Icon: SecurityIcon,
        title: 'Privacy',
        content: <a href="mailto:privacy@tinyrewards.net" style={{ color: 'inherit', textDecoration: 'none' }}>privacy@tinyrewards.net</a>,
    },
    {
        Icon: AccessTimeIcon,
        title: 'Response Time',
        content: 'We typically respond within 1–2 business days.',
    },
];

const topics = [
    'General enquiry',
    'Partnership opportunity',
    'Premium / enterprise plans',
    'Technical / API support',
    'Privacy or data request',
    'Report a bug',
    'Other',
];

export default function ContactPage() {
    const [name, setName]       = useState('');
    const [email, setEmail]     = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [state, setState]     = useState<FormState>('idle');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setState('submitting');
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, subject, message }),
            });
            if (res.ok) {
                setState('success');
                setName(''); setEmail(''); setSubject(''); setMessage('');
            } else {
                setState('error');
            }
        } catch {
            setState('error');
        }
    };

    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
                <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" fontWeight={700} gutterBottom>
                        Contact Us
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>
                        We'd love to hear from you. Drop us a message and we'll get back to you shortly.
                    </Typography>
                </Container>
            </Box>

            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} justifyContent="center">
                        {/* Contact info */}
                        <Grid item xs={12} lg={4}>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                Get in Touch
                            </Typography>

                            {contactInfo.map(({ Icon, title, content }) => (
                                <Box key={title} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                    <Box sx={{
                                        bgcolor: 'rgba(245,166,35,0.15)',
                                        borderRadius: 2,
                                        p: 1.5,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}>
                                        <Icon sx={{ color: 'primary.main' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{content}</Typography>
                                    </Box>
                                </Box>
                            ))}

                            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 1 }}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                    Common Topics
                                </Typography>
                                {topics.map(t => (
                                    <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                        <ChevronRightIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                                        <Typography variant="body2" color="text.secondary">{t}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        {/* Form */}
                        <Grid item xs={12} lg={6}>
                            <Card>
                                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                                    {state === 'success' ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 56, mb: 2 }} />
                                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                                Message Sent!
                                            </Typography>
                                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                                Thanks for reaching out. We'll be in touch shortly.
                                            </Typography>
                                            <Button variant="outlined" onClick={() => setState('idle')}>
                                                Send Another Message
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box component="form" onSubmit={handleSubmit}>
                                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                                Send a Message
                                            </Typography>

                                            {state === 'error' && (
                                                <Alert severity="error" sx={{ mb: 3 }}>
                                                    Something went wrong. Please try emailing us directly at hello@tinyrewards.net.
                                                </Alert>
                                            )}

                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Name"
                                                        fullWidth
                                                        required
                                                        value={name}
                                                        onChange={e => setName(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Email"
                                                        type="email"
                                                        fullWidth
                                                        required
                                                        value={email}
                                                        onChange={e => setEmail(e.target.value)}
                                                    />
                                                </Grid>
                                            </Grid>

                                            <TextField
                                                select
                                                label="Subject"
                                                fullWidth
                                                required
                                                value={subject}
                                                onChange={e => setSubject(e.target.value)}
                                                sx={{ mb: 2 }}
                                            >
                                                <MenuItem value="" disabled><em>Select a topic…</em></MenuItem>
                                                {topics.map(t => (
                                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                                ))}
                                            </TextField>

                                            <TextField
                                                label="Message"
                                                fullWidth
                                                required
                                                multiline
                                                rows={6}
                                                inputProps={{ minLength: 20 }}
                                                value={message}
                                                onChange={e => setMessage(e.target.value)}
                                                sx={{ mb: 3 }}
                                            />

                                            <Button
                                                type="submit"
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                disabled={state === 'submitting'}
                                                startIcon={state === 'submitting' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                                            >
                                                {state === 'submitting' ? 'Sending…' : 'Send Message'}
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
}
